"use client";

import React, { useMemo } from "react";
import { ArrowRight, BookOpen, Clock, RefreshCw } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { PostsProvider } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { decodeHtmlEntities } from "@/lib/utils";
import { useCategoryPosts } from "@/hooks/useCategoryPosts";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
const PRODUCT_SELECTION_CATEGORY_IDS = [150];
const ARTICLES_COPY = {
  sectionTag: {
    en: "Technical Knowledge Base",
    zh: "技术知识库",
    es: "Base de conocimientos técnicos",
    de: "Technische Wissensdatenbank",
    fr: "Base de connaissances techniques",
    pt: "Base de conhecimento técnico"
  },
  sectionTitle: {
    en: "Technical articles and mesh selection guides",
    zh: "技术文章与丝网选型指南",
    es: "Artículos técnicos y guías de selección de mallas",
    de: "Fachartikel und Leitfäden zur Gewebeauswahl",
    fr: "Articles techniques et guides de sélection",
    pt: "Artigos técnicos e guias de seleção de malhas"
  },
  sectionLead: {
    en: "Browse practical engineering insights, weave specifications, and filtration selection criteria.",
    zh: "浏览工程实践见解、编织技术规格及过滤选型标准。",
    es: "Explore análisis de ingeniería práctica, especificaciones de tejido y criterios de selección.",
    de: "Praxisorientierte Einblicke, Webart-Spezifikationen und Auswahlkriterien für die Filtration.",
    fr: "Découvrez des retours d'ingénierie, spécifications d'armure et critères de sélection.",
    pt: "Explore insights de engenharia, especificações de tecelagem e critérios de seleção."
  },
  viewAll: {
    en: "View all resources",
    zh: "查看所有资源",
    es: "Ver todos los recursos",
    de: "Alle Ressourcen anzeigen",
    fr: "Voir toutes les ressources",
    pt: "Ver todos os recursos"
  },
  fallbackCategory: {
    en: "Technical Guide",
    zh: "技术指南",
    es: "Guía técnica",
    de: "Technischer Leitfaden",
    fr: "Guide technique",
    pt: "Guia técnico"
  },
  fallbackExcerpt: {
    en: "Read technical mesh specifications, calculations, and filtration insights.",
    zh: "阅读技术丝网规格、工程计算与过滤技术见解。",
    es: "Lea especificaciones técnicas de mallas, cálculos y conocimientos de filtración.",
    de: "Technische Gewebespezifikationen, Berechnungen und Filtrationsanalysen lesen.",
    fr: "Consultez les spécifications techniques, calculs et analyses de filtration.",
    pt: "Leia especificações técnicas de malha, cálculos e insights de filtração."
  },
  readArticle: {
    en: "Read article",
    zh: "阅读文章",
    es: "Leer artículo",
    de: "Artikel lesen",
    fr: "Lire l'article",
    pt: "Ler artigo"
  },
  defaultImgAlt: {
    en: "Technical article",
    zh: "技术文章",
    es: "Artículo técnico",
    de: "Fachartikel",
    fr: "Article technique",
    pt: "Artigo técnico"
  },
  loadErrorTitle: {
    en: "Unable to load articles.",
    zh: "无法加载文章。",
    es: "No se pudieron cargar los artículos.",
    de: "Artikel konnten nicht geladen werden.",
    fr: "Impossible de charger les articles.",
    pt: "Não foi possível carregar os artigos."
  },
  loadErrorDesc: {
    en: "There was an issue fetching the latest technical posts. Please try again.",
    zh: "获取最新技术文章时出现问题，请重试。",
    es: "Ocurrió un problema al obtener los artículos técnicos. Por favor intente de nuevo.",
    de: "Beim Laden der technischen Artikel ist ein Fehler aufgetreten. Bitte erneut versuchen.",
    fr: "Un problème est survenu lors de la récupération des articles. Veuillez réessayer.",
    pt: "Houve um problema ao buscar os artigos técnicos. Tente novamente."
  },
  retry: {
    en: "Retry",
    zh: "重试",
    es: "Reintentar",
    de: "Erneut versuchen",
    fr: "Réessayer",
    pt: "Tentar novamente"
  },
  emptyTitle: {
    en: "No articles found",
    zh: "未找到相关文章",
    es: "No se encontraron artículos",
    de: "Keine Artikel gefunden",
    fr: "Aucun article trouvé",
    pt: "Nenhum artigo encontrado"
  },
  emptyDesc: {
    en: "Technical guides and mesh selection articles will appear here once published.",
    zh: "技术指南与丝网选型文章发布后将在此处显示。",
    es: "Las guías técnicas y artículos de selección aparecerán aquí una vez publicados.",
    de: "Technische Leitfäden und Auswahlarikel werden nach Veröffentlichung hier angezeigt.",
    fr: "Les guides techniques et articles de sélection apparaîtront ici dès leur publication.",
    pt: "Guias técnicos e artigos de seleção de malha aparecerão aqui após publicados."
  },
  prev: {
    en: "Previous",
    zh: "上一页",
    es: "Anterior",
    de: "Zurück",
    fr: "Précédent",
    pt: "Anterior"
  },
  next: {
    en: "Next",
    zh: "下一页",
    es: "Siguiente",
    de: "Weiter",
    fr: "Suivant",
    pt: "Próximo"
  }
};
function stripHtml(html?: string): string {
  if (!html) return "";
  const stripped = html.replace(/<[^>]*>?/gm, "").trim();
  return decodeHtmlEntities(stripped);
}
function ArticlesConsumer({
  lang
}: {
  lang: Lang;
}) {
  const {
    posts,
    totalFiltered,
    loading,
    error,
    refetch,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages,
    ready
  } = useCategoryPosts({
    categoryIds: PRODUCT_SELECTION_CATEGORY_IDS,
    pageSize: 6
  });
  if (loading || !ready) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({
        length: 6
      }).map((_, i) => <div key={i} data-index={i} className="flex flex-col gap-4 rounded-lg border border-border bg-card p-6">
            <Skeleton className="h-44 w-full rounded-lg" />
            <div className="flex gap-2">
              <Skeleton className="h-4 w-20 rounded-md" />
              <Skeleton className="h-4 w-16 rounded-md" />
            </div>
            <Skeleton className="h-6 w-3/4 rounded-md" />
            <Skeleton className="h-16 w-full rounded-md" />
            <Skeleton className="h-9 w-28 rounded-md mt-auto" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-card p-12 text-center">
        <p className="text-sm font-medium text-destructive mb-3">
          {pick(lang, ARTICLES_COPY.loadErrorTitle)}
        </p>
        <p className="mf-caption text-muted-foreground mb-6 max-w-md">
          {pick(lang, ARTICLES_COPY.loadErrorDesc)}
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()} className="gap-2">
          <RefreshCw className="size-3.5" aria-hidden="true" />
          <span>{pick(lang, ARTICLES_COPY.retry)}</span>
        </Button>
      </div>;
  }
  if (totalFiltered === 0 || posts.length === 0) {
    return <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border bg-card p-12 text-center">
        <BookOpen className="size-10 text-muted-foreground/60 mb-3" aria-hidden="true" />
        <h3 className="mf-h3 font-semibold text-foreground">
          {pick(lang, ARTICLES_COPY.emptyTitle)}
        </h3>
        <p className="text-sm text-muted-foreground mt-1 max-w-sm">
          {pick(lang, ARTICLES_COPY.emptyDesc)}
        </p>
      </div>;
  }
  return <div className="space-y-10">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {posts.map((post, i) => {
        const cleanExcerpt = stripHtml(post.excerpt);
        const categoryName = post.categories && post.categories.length > 0 ? decodeHtmlEntities(post.categories[0].name) : pick(lang, ARTICLES_COPY.fallbackCategory);
        const destination = post.link || `/TechnicalResourcesPage`;
        return <Card key={post.id} data-index={i} className="flex flex-col justify-between border-border bg-card rounded-lg transition-colors duration-200 hover:border-primary/40 overflow-hidden gap-0 py-0">
              <div>
                {post.featuredImage && <div className="mf-card-media mf-ratio-card w-full">
                    <img src={post.featuredImage} alt={post.title ? decodeHtmlEntities(post.title) : pick(lang, ARTICLES_COPY.defaultImgAlt)} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                  </div>}

                <CardHeader className="pt-5 pb-3">
                  <div className="flex flex-wrap items-center gap-2 mb-2.5">
                    <Badge variant="outline" className="mf-caption font-normal bg-muted border-border">
                      {categoryName}
                    </Badge>
                  </div>
                  <CardTitle className="mf-h3 font-semibold leading-snug text-foreground line-clamp-2">
                    <Link to={destination} className="text-foreground transition-colors hover:text-primary">
                      {decodeHtmlEntities(post.title)}
                    </Link>
                  </CardTitle>
                </CardHeader>

                <CardContent className="pb-4">
                  {cleanExcerpt ? <p className="text-sm text-muted-foreground line-clamp-3 leading-relaxed">
                      {cleanExcerpt}
                    </p> : <p className="text-sm text-muted-foreground italic">
                      {pick(lang, ARTICLES_COPY.fallbackExcerpt)}
                    </p>}
                </CardContent>
              </div>

              <CardFooter className="pt-0 pb-6">
                <Button variant="outline" size="sm" asChild className="w-full justify-between group">
                  <Link to={destination}>
                    <span>{pick(lang, ARTICLES_COPY.readArticle)}</span>
                    <ArrowRight className="size-3.5 text-muted-foreground group-hover:text-primary transition-colors" aria-hidden="true" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>;
      })}
      </div>

      {totalPages > 1 && <div className="flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-border pt-6">
          <p className="mf-caption text-muted-foreground">
            {lang === "zh" ? <>
                显示第 <span className="font-medium text-foreground">{currentPage}</span> 页 / 共{" "}
                <span className="font-medium text-foreground">{totalPages}</span> 页
              </> : <>
                Showing page <span className="font-medium text-foreground">{currentPage}</span> of{" "}
                <span className="font-medium text-foreground">{totalPages}</span>
              </>}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => prevPage()} disabled={!hasPrev}>
              {pick(lang, ARTICLES_COPY.prev)}
            </Button>
            <Button variant="outline" size="sm" onClick={() => nextPage()} disabled={!hasNext}>
              {pick(lang, ARTICLES_COPY.next)}
            </Button>
          </div>
        </div>}
    </div>;
}
export default function Articles() {
  const {
    lang
  } = useLanguage();
  const wp_query = useMemo(() => {
    return new WP_Query({
      post_type: "post",
      per_page: 100,
      order: "desc",
      orderby: "date",
      status: ["publish"],
      categories: PRODUCT_SELECTION_CATEGORY_IDS
    });
  }, []);
  return <section id="recentarticlesgrid" data-nav="light" className="w-full bg-background mf-section border-b border-border">
      <div className="mf-container">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mf-caption font-semibold uppercase tracking-wider text-primary mb-2">
              <Clock className="size-3.5 text-primary" aria-hidden="true" />
              <span>{pick(lang, ARTICLES_COPY.sectionTag)}</span>
            </div>
            <h2 className="mf-h2 font-semibold tracking-tight text-foreground text-balance">
              {pick(lang, ARTICLES_COPY.sectionTitle)}
            </h2>
            <p className="mt-3 mf-lead text-muted-foreground leading-relaxed">
              {pick(lang, ARTICLES_COPY.sectionLead)}
            </p>
          </div>
          <div className="shrink-0">
            <Button variant="outline" asChild>
              <Link to="/TechnicalResourcesPage">
                {pick(lang, ARTICLES_COPY.viewAll)}
              </Link>
            </Button>
          </div>
        </div>

        <PostsProvider key="product-selection-articles" wp_query={wp_query}>
          <ArticlesConsumer lang={lang} />
        </PostsProvider>
      </div>
    </section>;
}